import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, ImageOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ImageModal = ({ isOpen, onClose, imageUrl, fileName }) => {
  const { t } = useTranslation('chat');
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    if (isOpen && imageUrl) {
      setImgError(false);
      setImgLoaded(false);
    }
  }, [isOpen, imageUrl]);

  if (!isOpen || !imageUrl) return null;

  const handleDownload = async () => {
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(t('imageModal.downloadFailed'));
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Use original filename if available, otherwise extract from URL or use default
      let downloadName = fileName;
      if (!downloadName) {
        // Try to extract filename from URL
        const urlParts = imageUrl.split('/');
        const urlFileName = urlParts[urlParts.length - 1];
        // Remove timestamp prefix if exists (format: timestamp_filename.ext)
        if (urlFileName.includes('_')) {
          downloadName = urlFileName.substring(urlFileName.indexOf('_') + 1);
        } else {
          downloadName = urlFileName || 'image.jpg';
        }
      }
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (error) {
      console.error('Download error:', error);
      alert(t('imageModal.downloadFailedAlert', { reason: error.message || t('notes:errors.unknownError') }));
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100000]"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100001] flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="relative flex items-center justify-center rounded-2xl shadow-2xl overflow-hidden bg-[#0d1424]"
              style={{ maxWidth: '90vw', maxHeight: '90vh' }}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors backdrop-blur-sm"
                title={t('common:close')}
              >
                <X className="w-5 h-5" />
              </button>

              {/* Download Button */}
              {!imgError && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDownload(); }}
                  className="absolute top-3 left-3 z-10 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors backdrop-blur-sm"
                  title={t('imageModal.download')}
                >
                  <Download className="w-5 h-5" />
                </button>
              )}

              {/* Loading spinner */}
              {!imgLoaded && !imgError && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-[#3d8ef0] border-t-transparent rounded-full animate-spin" />
                </div>
              )}

              {/* Error state */}
              {imgError && (
                <div className="flex flex-col items-center justify-center gap-3 p-16 text-center">
                  <ImageOff className="w-12 h-12 text-[#3d5470]" />
                  <p className="text-sm text-[#4d6a8a]">{t('imageModal.loadFailed')}</p>
                  <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#3d8ef0] hover:underline" onClick={e => e.stopPropagation()}>
                    {t('imageModal.openLink')}
                  </a>
                </div>
              )}

              {/* Image */}
              {!imgError && (
                <img
                  src={imageUrl}
                  alt={fileName || t('imageModal.imageAlt')}
                  className="block max-w-[88vw] max-h-[88vh] object-contain rounded-xl"
                  style={{ display: imgLoaded ? 'block' : 'block', opacity: imgLoaded ? 1 : 0, transition: 'opacity 0.2s' }}
                  onLoad={() => setImgLoaded(true)}
                  onError={() => { setImgError(true); setImgLoaded(false); }}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ImageModal;

